import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import './ButtonDialog.css';

export type ButtonDialogAlign = 'left' | 'right' | 'center';

export interface ButtonDialogProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  message: string;
  confirmText?: string;
  cancelText?: string;
  buttonRef: React.RefObject<HTMLElement>;
  /** 定位锚点元素，优先于 buttonRef（用于从非主按钮打开时定位到点击的按钮） */
  anchorRef?: React.RefObject<HTMLElement | null>;
  align?: ButtonDialogAlign;
  container?: HTMLElement | null; // 容器元素，如果不提供则使用 document.body
}

const ButtonDialog: React.FC<ButtonDialogProps> = ({
  visible,
  onClose,
  onConfirm,
  message,
  confirmText,
  cancelText,
  buttonRef,
  anchorRef,
  align = 'center',
  container = null
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const resolvedConfirmText = confirmText ?? t('common.confirm');
  const resolvedCancelText = cancelText ?? t('common.cancel');

  const getAnchor = useCallback(
    () => (anchorRef?.current != null ? anchorRef.current : buttonRef.current),
    [anchorRef, buttonRef]
  );

  // 计算弹窗位置
  useEffect(() => {
    const anchor = getAnchor();
    if (!visible || !anchor || !containerRef.current) return;

    const dialog = containerRef.current;
    
    // 获取锚点（按钮）的位置
    const buttonRect = anchor.getBoundingClientRect();
    
    // 获取容器元素（用于计算相对位置）
    const containerElement = container || document.body;
    const containerRect = containerElement === document.body 
      ? { left: 0, top: 0 }
      : containerElement.getBoundingClientRect();
    
    // 计算弹窗宽度（如果还没有渲染，使用默认值）
    const dialogWidth = dialog.offsetWidth || 260;
    
    // 计算 left 位置
    let leftPosition: number;
    if (align === 'left') {
      // 左对齐：按钮左边框与弹窗左边框对齐
      leftPosition = buttonRect.left - containerRect.left;
    } else if (align === 'right') {
      // 右对齐：按钮右边框与弹窗右边框对齐
      leftPosition = buttonRect.right - containerRect.left - dialogWidth;
    } else {
      // 居中对齐：弹窗与按钮居中对齐
      leftPosition = buttonRect.left - containerRect.left + (buttonRect.width - dialogWidth) / 2;
    }
    
    // 计算 top 位置（按钮下方 4px）
    const topPosition = buttonRect.bottom - containerRect.top + 4;
    
    // 设置位置
    dialog.style.left = `${leftPosition}px`;
    dialog.style.top = `${topPosition}px`;
  }, [visible, getAnchor, align, container]);

  // 监听窗口大小变化和滚动，重新计算位置
  useEffect(() => {
    if (!visible) return;

    const updatePosition = () => {
      const anchor = getAnchor();
      if (!anchor || !containerRef.current) return;
      
      const button = anchor;
      const dialog = containerRef.current;
      const buttonRect = button.getBoundingClientRect();
      const containerElement = container || document.body;
      const containerRect = containerElement === document.body 
        ? { left: 0, top: 0 }
        : containerElement.getBoundingClientRect();
      const dialogWidth = dialog.offsetWidth || 260;
      
      let leftPosition: number;
      if (align === 'left') {
        leftPosition = buttonRect.left - containerRect.left;
      } else if (align === 'right') {
        leftPosition = buttonRect.right - containerRect.left - dialogWidth;
      } else {
        leftPosition = buttonRect.left - containerRect.left + (buttonRect.width - dialogWidth) / 2;
      }
      
      const topPosition = buttonRect.bottom - containerRect.top + 4;
      dialog.style.left = `${leftPosition}px`;
      dialog.style.top = `${topPosition}px`;
    };

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [visible, getAnchor, align, container]);

  // 点击外部关闭
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // 延迟添加事件监听，避免立即触发
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onClose]);

  // ESC 键关闭
  useEffect(() => {
    if (!visible) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible, onClose]);

  if (!visible) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const dialogContent = (
    <div ref={containerRef} className="button-dialog-container">
      <div className="button-dialog">
        <div className="button-dialog-message">{message}</div>
        <div className="button-dialog-actions">
          <button
            className="button-dialog-btn button-dialog-btn-cancel"
            onClick={onClose}
          >
            {resolvedCancelText}
          </button>
          <button
            className="button-dialog-btn button-dialog-btn-confirm"
            onClick={handleConfirm}
          >
            {resolvedConfirmText}
          </button>
        </div>
      </div>
    </div>
  );

  // 使用 Portal 渲染到容器或 body
  const targetContainer = container || document.body;
  return createPortal(dialogContent, targetContainer);
};

export default ButtonDialog;

